import _ from "lodash";

import { supabase } from "@/utils/supabase";
import { idToModelUrl } from "@/constants";
import { isLocal } from "@/utils/env-utils";

// SUPABASE NOTE
// upsert는 primary key가 잇어야 한다

const camelToSnakeStr = (str) => str.replace(/([a-z])([A-Z])/g, "$1_$2").toLowerCase();
const camelToSnakeObj = (obj) => _.transform(obj, (result, value, key) => (result[camelToSnakeStr(key)] = value));

const snakeToCamelStr = (str) =>
  str.replace(/([-_][a-z])/g, (group) => group.toUpperCase().replace("-", "").replace("_", ""));

// 진짜 이상하네이거 isReverse가 true일때만 backgroundFilename이 나옴
// const snakeToCamelObj = (obj) => _.transform(obj, (result, value, key) => (result[snakeToCamelStr(key)] = value), {});

const snakeToCamelObj = (obj) => {
  const result = {};
  Object.entries(obj).forEach(([key, value]) => {
    result[snakeToCamelStr(key)] = value;
  });
  return result;
};

// local과 prod 모두 동일 DB의 live_ 테이블 사용 (dev_ 폐기)
const PREFIX = "live_";

const extractToken = (accessToken) => {
  return {
    uuid: accessToken,
  };
};


let _authUserId = null; // = auth.users.id (user_account 키)
export const setAuthUserId = (id) => {
  _authUserId = id;
};
// 인증 컨텍스트 준비 여부 확인용(로그인 부트스트랩 전 조기 호출 가드).
export const getAuthUserId = () => _authUserId;

// OBS(anon)는 _authUserId가 없으므로 토큰 검증으로 받은 pet_id를 직접 주입(pet_id 키 테이블용).
// 로그인 컨텍스트에선 미설정 → fetchActivePetId()로 선택 펫(없으면 첫 펫)을 폴백.
let _petId = null;
export const setPetId = (id) => {
  _petId = id;
};

// 로그인 사용자가 /pet/select에서 고른 활성 펫(user_status.selected_pet_id) 캐시.
// App.vue 인증 부트스트랩에서 1회 로드, 펫 변경 시 갱신. 미설정이면 첫 펫 폴백.
let _selectedPetId = null;
export const setSelectedPetId = (id) => {
  _selectedPetId = id;
};

///
export const fetchMyProfile = async () => {
  const { data, error } = await supabase //
    .from("user_account")
    .select("*")
    .eq("id", _authUserId)
    .single();

  const { name, created_at } = data || {};

  return {
    name,
    createdAt: created_at,
  };
};

/**
 * LEVEL & EXP
 */
const TABLE_LEVEL_EXP = "pet_agg_growth"; // 성장(레벨/경험치). pet_id 키. RLS 공개(anon OBS write).

export const upsertVirtupetLevel = async (level, totalExp) => {
  const petId = await fetchActivePetId();
  if (!petId) throw new Error("PET_NOT_FOUND");

  const { error } = await supabase //
    .from(TABLE_LEVEL_EXP)
    .upsert({ pet_id: petId, level, total_exp: totalExp }, { onConflict: "pet_id" });

  if (error) throw error;
};

export const fetchMyVirtupetLevelMeta = async () => {
  const petId = await fetchActivePetId();
  if (!petId) return null;

  const { data, error } = await supabase //
    .from(TABLE_LEVEL_EXP)
    .select("*")
    .eq("pet_id", petId)
    .maybeSingle();

  if (error) {
    console.error(error);
    return null;
  }
  if (!data) return null;

  const { level, total_exp } = data;
  return { level, totalExp: total_exp };
};

/////////////////////////////

export const updateProfileName = async (name, imageUrl) => {
  if (!_authUserId) throw new Error("AUTH_USER_ID_NOT_FOUND");

  const { data, error } = await supabase //
    .from("user_account")
    .update({ name, image_url: imageUrl })
    .eq("id", _authUserId);

  if (error) throw error;
};

// 펫 1마리 생성: pet_profile + 레벨/경험치(level 1) 행. 새 pet id 반환. (온보딩/펫추가 공용)
//  pet_profile.model_url 에는 모델 id(카탈로그 key, 예 "bear")를 저장. 재생 직전 idToModelUrl로 해석.
const _createPet = async (modelIndex, meta = {}) => {
  const { name, age, mbti, gender, job, planet } = meta;
  const { data: pet, error: petErr } = await supabase
    .from("pet_profile")
    .insert({ owner_id: _authUserId, model_url: modelIndex, name, age, mbti, gender, job, planet })
    .select("id")
    .single();
  if (petErr) {
    if (petErr.code === "23505") throw new Error("E_PET_NAME_TAKEN"); // 이름 글로벌 유일 위반
    throw petErr;
  }

  const { error: lvlErr } = await supabase.from("pet_agg_growth").upsert({ pet_id: pet.id }, { onConflict: "pet_id" });
  if (lvlErr) throw lvlErr;

  return pet.id;
};

const UserAccount = (() => {
  return {
    // 계정: user_account (auth.users.id 기준, RLS on)
    whoAmI: async () => {
      const { data, error } = await supabase //
        .from("user_account")
        .select("*")
        .eq("id", _authUserId)
        .single();

      if (error) console.error(error);

      if (!data) return null;
      else return data;
    },
    // 스트리머 계정 프로비저닝 (idempotent). 옵션 B: handle_new_user 자동생성을 제거하면
    //  스트리머 경로에서 이 ensure가 user_account/onboarding 생성을 담당한다.
    //  is_streamer 플래그는 user_account insert 트리거(sync_role_flag)가 병합.
    async ensure() {
      if (!_authUserId) throw new Error("AUTH_USER_ID_NOT_FOUND");
      const { data: { session } = {} } = await supabase.auth.getSession();
      const u = (session && session.user) || {};
      const meta = u.user_metadata || {};
      await supabase.from("user_account").upsert(
        {
          id: _authUserId,
          email: u.email || null,
          name: meta.full_name || meta.name || null,
          image_url: meta.avatar_url || meta.picture || null,
        },
        { onConflict: "id", ignoreDuplicates: true },
      );
      await supabase
        .from("user_account_onboarding")
        .upsert({ user_id: _authUserId, is_complete: false }, { onConflict: "user_id", ignoreDuplicates: true });
    },

    // 온보딩 확정: 펫 도메인 행 생성(email 키) + is_complete=true.
    // 라이선스/billing_purchase는 실제 결제 때만 생성 → 신규 유저는 FREE(예비집사).
    // meta: VirtupetMeta ({ name, age, mbti, gender, job, planet }) — 컬럼명이 그대로라 직접 매핑
    async completeOnboarding(modelUrl, meta = {}) {
      // 첫 펫 생성 + 온보딩 완료 표시. 새 pet id 반환(호출측에서 활성 펫 선택).
      const newPetId = await _createPet(modelUrl, meta);

      const { error } = await supabase
        .from("user_account_onboarding")
        .update({ is_complete: true })
        .eq("user_id", _authUserId);
      if (error) throw error;
      return newPetId;
    },
    // 펫 추가(이미 온보딩 완료 사용자). is_complete 변경 안 함. 새 pet id 반환.
    async createPet(modelUrl, meta = {}) {
      return await _createPet(modelUrl, meta);
    },
    async deleteAccount(email) {
      // pet_broadcast_setting · pet_agg_growth · pet_agg_vital 등은 pet_profile FK on delete cascade로 함께 삭제됨
      if (_authUserId) await supabase.from("pet_profile").delete().eq("owner_id", _authUserId);
      await supabase.from("user_account").delete().eq("email", email);
    },
    async login(email, password) {
      // Verify credentials via Supabase Auth (bcrypt, server-side).
      const { data: authData, error: authError } = await supabase.auth.signInWithPassword({ email, password });
      if (authError) {
        // 실제 원인(잘못된 자격증명 / 이메일 미인증 / auth.users 없음)을 콘솔에 노출. LoginPage는 null만 보고 E_NOT_FOUND 처리.
        console.error("login: signInWithPassword failed →", authError.message, authError);
        return null;
      }

      _authUserId = authData.user.id; // user_account 키

      // user_account 행은 auth.users 트리거(on_auth_user_created → handle_new_user)가 보장한다.
      // 여기서는 조회만. (드물게 행이 없어도) null-safe하게 최소 객체 반환.
      const { data } = await supabase //
        .from("user_account")
        .select("*")
        .eq("id", _authUserId)
        .single();

      return snakeToCamelObj(data || { id: _authUserId, email: authedEmail });
    },
    async changePassword(password) {
      // Update the password in Supabase Auth (auth.users, bcrypt).
      const { error } = await supabase.auth.updateUser({ password });
      if (error) throw error;
    },
    // 닉네임(표시 이름)만 변경 — user_account.name (auth.uid 기준)
    async updateNickname(name) {
      if (!_authUserId) throw new Error("AUTH_USER_ID_NOT_FOUND");
      const { error } = await supabase.from("user_account").update({ name }).eq("id", _authUserId);
      if (error) throw error;
    },
    // 프로필 이미지 URL 변경 — user_account.image_url (auth.uid 기준)
    async updateImageUrl(imageUrl) {
      if (!_authUserId) throw new Error("AUTH_USER_ID_NOT_FOUND");
      const { error } = await supabase.from("user_account").update({ image_url: imageUrl }).eq("id", _authUserId);
      if (error) throw error;
    },
    // UI 언어 변경 — user_account.locale (ISO 639-1: ko/en/ja). localStorage 대체 영속.
    async updateLocale(locale) {
      if (!_authUserId) throw new Error("AUTH_USER_ID_NOT_FOUND");
      const { error } = await supabase.from("user_account").update({ locale }).eq("id", _authUserId);
      if (error) throw error;
    },
    // 국가 변경 — user_account.country_code (ISO 3166-1 alpha-2: KR/US/JP …).
    async updateCountryCode(countryCode) {
      if (!_authUserId) throw new Error("AUTH_USER_ID_NOT_FOUND");
      const { error } = await supabase.from("user_account").update({ country_code: countryCode }).eq("id", _authUserId);
      if (error) throw error;
    },
  };
})();

const UserAccountOnboarding = (() => {
  return {
    // 온보딩 완료 여부 (행 없거나 에러면 미완료로 취급)
    async fetchIsComplete() {
      const { data, error } = await supabase //
        .from("user_account_onboarding")
        .select("is_complete")
        .eq("user_id", _authUserId)
        .single();

      if (error) return false;
      return !!data.is_complete;
    },
  };
})();

// 팬 계정(fan_account) API는 SupabaseFanApi.Account 로 이관됨.

const BillingPurchase = (() => {
  return {
    // 현재 활성 billing_purchase(now ∈ [started_at, ended_at])로 플랜 판정.
    // 활성 행 없으면 FREE(예비집사). streamer_license 폐기.
    fetchLicense: async () => {
      const nowIso = new Date().toISOString();

      const { data } = await supabase //
        .from("billing_purchase")
        .select("*")
        .lte("started_at", nowIso)
        .gte("ended_at", nowIso)
        .order("ended_at", { ascending: false })
        .limit(1);

      const active = data && data[0];

      return {
        planType: active ? "MEMBERSHIP" : "FREE",
        expiredAt: active ? active.ended_at : null,
      };
    },
  };
})();

// owner의 첫 펫(index 0) id — 활성 펫 미선택 시 폴백.
const fetchFirstPetId = async () => {
  if (!_authUserId) throw new Error("AUTH_USER_ID_NOT_FOUND");
  const { data, error } = await supabase
    .from("pet_profile")
    .select("id")
    .eq("owner_id", _authUserId)
    .order("created_at", { ascending: true })
    .limit(1)
    .maybeSingle();
  if (error) throw error;
  return data ? data.id : null;
};

// 활성 펫 id (pet_id 키 테이블들 공유): OBS 주입(_petId) > 로그인 선택(_selectedPetId) > 첫 펫(폴백).
const fetchActivePetId = async () => {
  if (_petId) return _petId;
  if (_selectedPetId) return _selectedPetId;
  return await fetchFirstPetId();
};

const PetProfile = (() => {
  const TABLE_NAME = "pet_profile"; // owner_id(uuid) 키. live_ 아님.

  return {
    // 내 펫 수 (owner_id RLS). 구독관리에서 'N / 제한 M' 표시용.
    fetchCount: async () => {
      if (!_authUserId) throw new Error("AUTH_USER_ID_NOT_FOUND");
      const { count, error } = await supabase
        .from(TABLE_NAME)
        .select("id", { count: "exact", head: true })
        .eq("owner_id", _authUserId);
      if (error) {
        console.error(error);
        return 0;
      }
      return count || 0;
    },
    // 반환값: 해석된 CDN model_url. 저장은 id(또는 커스텀 경로) → idToModelUrl로 해석. 활성 펫(선택 > 첫 펫).
    fetchModelIndex: async () => {
      const petId = await fetchActivePetId();
      if (!petId) return null;
      const { data, error } = await supabase.from(TABLE_NAME).select("model_url").eq("id", petId).maybeSingle();
      if (error) throw error;
      return data ? idToModelUrl(data.model_url) : null;
    },
    // modelIndex 인자 = 모델 id(카탈로그 key). pet_profile엔 id 그대로 저장.
    changeModelIndex: async (modelIndex) => {
      const petId = await fetchActivePetId();
      if (!petId) throw new Error("PET_NOT_FOUND");
      const { error } = await supabase.from(TABLE_NAME).update({ model_url: modelIndex }).eq("id", petId);
      if (error) throw error;
    },
    fetchProfile: async () => {
      const petId = await fetchActivePetId();
      if (!petId) return null;
      const { data, error } = await supabase.from(TABLE_NAME).select("*").eq("id", petId).maybeSingle();
      if (error) console.error(error);
      if (!data) return null;
      const a = _.omit(data, ["id", "owner_id", "created_at", "updated_at"]);
      return snakeToCamelObj(a);
    },
    updateProfile: async (obj) => {
      const petId = await fetchActivePetId();
      if (!petId) throw new Error("PET_NOT_FOUND");
      const { error } = await supabase.from(TABLE_NAME).update(camelToSnakeObj(obj)).eq("id", petId);
      if (error) {
        if (error.code === "23505") throw new Error("E_PET_NAME_TAKEN"); // 이름 글로벌 유일 위반
        throw error;
      }
    },
    // 펫 이름 사용가능 여부 (글로벌 유일, 대소문자무시+trim). excludePetId: 이름변경 시 본인 펫 제외.
    isNameAvailable: async (name, excludePetId = null) => {
      const { data, error } = await supabase.rpc("is_pet_name_available", {
        p_name: name,
        p_exclude_pet_id: excludePetId,
      });
      if (error) throw error;
      return data === true;
    },
    // 내 모든 펫 (펫 선택 페이지용). created_at 오름차순 = 슬롯 순서.
    fetchAllPets: async () => {
      if (!_authUserId) throw new Error("AUTH_USER_ID_NOT_FOUND");
      const { data, error } = await supabase
        .from(TABLE_NAME)
        .select("id, name, model_url, created_at")
        .eq("owner_id", _authUserId)
        .order("created_at", { ascending: true });
      if (error) {
        console.error(error);
        return [];
      }
      return (data || []).map(snakeToCamelObj);
    },
    // OBS(anon, 세션 없음)용: pet_id로 공개 필드만 RPC 조회 (테이블 직접 노출 안 함)
    fetchPublicPet: async (petId) => {
      if (!petId) return null;
      const { data, error } = await supabase.rpc("get_public_pet", { p_pet_id: petId });
      if (error) {
        console.error(error);
        return null;
      }
      return (Array.isArray(data) ? data[0] : data) || null;
    },
  };
})();

const PetBroadcastSetting = (() => {
  const TABLE_NAME = "pet_broadcast_setting"; // pet_id(uuid) 키 = 로그인 사용자의 활성 펫.

  return {
    // 로그인 사용자 활성 펫의 설정 (RLS=펫 소유자). 미설정(0행)이 정상 → maybeSingle.
    fetchBroadcastSetting: async () => {
      const petId = await fetchActivePetId();
      if (!petId) return null;

      const { data, error } = await supabase //
        .from(TABLE_NAME)
        .select("*")
        .eq("pet_id", petId)
        .maybeSingle();

      if (error) console.error(error);
      if (!data) return null;
      return snakeToCamelObj(data);
    },
    updateBroadcastSetting: async (obj) => {
      const petId = await fetchActivePetId();
      if (!petId) throw new Error("PET_NOT_FOUND");

      const { error } = await supabase //
        .from(TABLE_NAME)
        .upsert({ pet_id: petId, ...camelToSnakeObj(obj) }, { onConflict: "pet_id" });

      if (error) throw error;
    },
    // OBS(anon, 세션 없음)용: pet_id로 공개 방송설정 RPC (테이블 직접 노출 안 함)
    fetchPublicBroadcastSetting: async (petId) => {
      if (!petId) return null;
      const { data, error } = await supabase.rpc("get_public_broadcast_setting", { p_pet_id: petId });
      if (error) {
        console.error(error);
        return null;
      }
      return data ? snakeToCamelObj(data) : null;
    },
  };
})();

// REACTION — 리액션 테이블(reaction). 소유는 별도 매핑 user_reaction(user_id, reaction_id)로 분리.
//  - reaction: creator_id(작성자) + is_system(전역 공통) + 콘텐츠. (구 user_type/owner_id 제거)
//  - 구분 type은 저장 안 함(파생, 필터 전용): is_system→'SYSTEM' · 소유&creator==나→'STREAMER' · 소유&creator≠나→'FAN'
//  - 제어판 fetchAll: ① is_system + ② user_reaction(내 소유) → reaction 조립. RLS = is_system OR creator=나 OR 매핑소유.
//  - 오버레이(익명): get_public_reactions(petId) → is_system OR user_reaction(pet 소유자).
//  - 참조키 = id('rct_<hash>'). SYSTEM은 slug(안정 핸들) 보유, 나머지는 null.
const Reaction = (() => {
  const TABLE_NAME = "reaction";
  const MAP_TABLE = "user_reaction";

  const genId = () => {
    const bytes = new Uint8Array(10);
    window.crypto.getRandomValues(bytes);
    return "rct_" + Array.from(bytes, (b) => b.toString(16).padStart(2, "0")).join("");
  };

  // type은 클라 전용 파생값. 호출측이 결정해 넘김(없으면 is_system 기준 기본).
  const normalize = (r, type) => ({
    id: r.id,
    slug: r.slug || null,
    type: type || (r.is_system ? "SYSTEM" : "STREAMER"),
    isSystem: !!r.is_system,
    isHide: !!r.is_hide,
    label: r.label || "",
    tags: r.tags || [],
    cmds: r.cmds || [],
    creatorId: r.creator_id || null,
    createdAt: r.created_at || null,
  });

  return {
    // 제어판(로그인): ① 시스템 + ② 내 소유(user_reaction) 조립, type 파생.
    fetchAll: async () => {
      const uid = getAuthUserId();
      // ① 시스템 (전역 공통)
      const { data: sys, error: e1 } = await supabase.from(TABLE_NAME).select("*").eq("is_system", true);
      if (e1) throw e1;
      const list = (sys || []).map((r) => normalize(r, "SYSTEM"));
      if (uid) {
        // ② 내 소유 reaction_id
        const { data: owned, error: e2 } = await supabase.from(MAP_TABLE).select("reaction_id").eq("user_id", uid);
        if (e2) throw e2;
        const ownedIds = (owned || []).map((r) => r.reaction_id);
        // ③ 소유 리액션 → creator==나 'STREAMER', 아니면 'FAN'
        if (ownedIds.length) {
          const { data: rows, error: e3 } = await supabase.from(TABLE_NAME).select("*").in("id", ownedIds);
          if (e3) throw e3;
          (rows || []).forEach((r) => list.push(normalize(r, r.creator_id === uid ? "STREAMER" : "FAN")));
        }
      }
      // id 중복 제거(방어)
      const byId = {};
      list.forEach((r) => (byId[r.id] = r));
      return Object.values(byId);
    },
    // 오버레이(익명): 공개 RPC (type 미사용 — is_system 기준 기본 파생)
    fetchPublic: async (petId) => {
      if (!petId) return [];
      const { data, error } = await supabase.rpc("get_public_reactions", { p_pet_id: petId });
      if (error) {
        console.error(error);
        return [];
      }
      return (data || []).map((r) => normalize(r));
    },
    // 리액션 생성 → id 반환. creator=나, 비시스템. 스트리머 소유 즉시 매핑(user_reaction).
    create: async ({ label = "", tags = [], cmds = [] }) => {
      const uid = getAuthUserId();
      if (!uid) throw new Error("E_NOT_LOGGED_IN");
      const id = genId();
      const { error } = await supabase
        .from(TABLE_NAME)
        .insert({ id, creator_id: uid, is_system: false, is_hide: false, label, tags, cmds });
      if (error) throw error;
      const { error: e2 } = await supabase.from(MAP_TABLE).insert({ user_id: uid, reaction_id: id });
      if (e2) throw e2;
      return id;
    },
    update: async (id, patch = {}) => {
      const allowed = { updated_at: new Date().toISOString() };
      if (patch.label != null) allowed.label = patch.label;
      if (patch.tags != null) allowed.tags = patch.tags;
      if (patch.cmds != null) allowed.cmds = patch.cmds;
      const { error } = await supabase.from(TABLE_NAME).update(allowed).eq("id", id);
      if (error) throw error;
    },
    remove: async (id) => {
      const { error } = await supabase.from(TABLE_NAME).delete().eq("id", id);
      if (error) throw error;
    },
    // rows → { byId, bySlug, list } 편의 변환 (resolver/셀렉터용)
    toMaps: (rows = []) => {
      const byId = {};
      const bySlug = {};
      rows.forEach((r) => {
        byId[r.id] = r;
        if (r.slug) bySlug[r.slug] = r;
      });
      return { byId, bySlug, list: rows };
    },
  };
})();

// 방송 룰 (owner 중심 정규화 테이블). 매칭/발동은 클라 매처 담당.
//  - 제어판: fetchMine(RLS=본인) + CRUD
//  - 오버레이(익명): BroadcastRules.fetchPublic(petId) → 공개 RPC 조립본
const _genRuleId = (prefix) => {
  const bytes = new Uint8Array(10);
  window.crypto.getRandomValues(bytes);
  return prefix + "_" + Array.from(bytes, (b) => b.toString(16).padStart(2, "0")).join("");
};

// 세션(휘발) 키워드룰 — owner의 최신 date 세트를 공개 RPC로 조회(오버레이는 RLS-authed 아님).
//  rnd가 reaction_keyword_session_rule에 적재(weight=빈도 순위). rnd /rules/session/last 대체.
const ReactionKeywordSessionRule = {
  fetchLatest: async (ownerId) => {
    if (!ownerId) return [];
    const { data, error } = await supabase.rpc("get_public_session_rules", { p_owner_id: ownerId });
    if (error) {
      console.error(error);
      return [];
    }
    // setSessionRules/rulesToWeightedWords가 기대하는 shape(빈도순 유지, label→reaction_label)
    return (data || []).map((r) => ({
      keywords: r.keywords || [],
      reaction_id: r.reaction_id,
      reaction_label: r.label || "",
    }));
  },
};

const ReactionKeywordRule = (() => {
  const TABLE = "reaction_keyword_rule";
  const normalize = (r) => ({
    id: r.id,
    isMentionMode: !!r.is_mention_mode,
    keywords: r.keywords || [],
    reactionId: r.reaction_id || null,
    isEnabled: r.is_enabled !== false,
    label: r.label || "",
  });
  return {
    fetchMine: async () => {
      const { data, error } = await supabase.from(TABLE).select("*").order("created_at");
      if (error) throw error;
      return (data || []).map(normalize);
    },
    create: async ({ isMentionMode = false, keywords = [], reactionId = null, label = "" }) => {
      const ownerId = getAuthUserId();
      if (!ownerId) throw new Error("E_NOT_LOGGED_IN");
      const id = _genRuleId("wordrule");
      const { error } = await supabase
        .from(TABLE)
        .insert({ id, owner_id: ownerId, is_mention_mode: isMentionMode, keywords, reaction_id: reactionId, label });
      if (error) throw error;
      return id;
    },
    update: async (id, patch = {}) => {
      const row = { updated_at: new Date().toISOString() };
      if (patch.isMentionMode != null) row.is_mention_mode = patch.isMentionMode;
      if (patch.keywords != null) row.keywords = patch.keywords;
      if (patch.reactionId !== undefined) row.reaction_id = patch.reactionId;
      if (patch.isEnabled != null) row.is_enabled = patch.isEnabled;
      if (patch.label != null) row.label = patch.label;
      const { error } = await supabase.from(TABLE).update(row).eq("id", id);
      if (error) throw error;
    },
    remove: async (id) => {
      const { error } = await supabase.from(TABLE).delete().eq("id", id);
      if (error) throw error;
    },
  };
})();

const ReactionDonationRule = (() => {
  const TABLE = "reaction_donation_rule";
  const normalize = (r) => ({
    id: r.id,
    minAmount: r.min_amount,
    maxAmount: r.max_amount,
    reactionId: r.reaction_id || null,
  });
  return {
    fetchMine: async () => {
      const { data, error } = await supabase.from(TABLE).select("*").order("min_amount");
      if (error) throw error;
      return (data || []).map(normalize);
    },
    create: async ({ minAmount = 0, maxAmount = null, reactionId = null }) => {
      const ownerId = getAuthUserId();
      if (!ownerId) throw new Error("E_NOT_LOGGED_IN");
      const id = _genRuleId("donarule");
      const { error } = await supabase
        .from(TABLE)
        .insert({ id, owner_id: ownerId, min_amount: minAmount, max_amount: maxAmount, reaction_id: reactionId });
      if (error) throw error;
      return id;
    },
    update: async (id, patch = {}) => {
      const row = { updated_at: new Date().toISOString() };
      if (patch.minAmount != null) row.min_amount = patch.minAmount;
      if (patch.maxAmount !== undefined) row.max_amount = patch.maxAmount;
      if (patch.reactionId !== undefined) row.reaction_id = patch.reactionId;
      const { error } = await supabase.from(TABLE).update(row).eq("id", id);
      if (error) throw error;
    },
    remove: async (id) => {
      const { error } = await supabase.from(TABLE).delete().eq("id", id);
      if (error) throw error;
    },
  };
})();

const UserBroadcastSetting = (() => {
  const TABLE = "user_broadcast_setting";
  return {
    fetchMine: async () => {
      const { data, error } = await supabase.from(TABLE).select("*").maybeSingle();
      if (error) {
        console.error(error);
        return { donationEnabled: true };
      }
      return { donationEnabled: data ? data.donation_enabled !== false : true };
    },
    upsert: async ({ donationEnabled }) => {
      const ownerId = getAuthUserId();
      if (!ownerId) throw new Error("E_NOT_LOGGED_IN");
      const { error } = await supabase
        .from(TABLE)
        .upsert(
          { owner_id: ownerId, donation_enabled: donationEnabled, updated_at: new Date().toISOString() },
          { onConflict: "owner_id" },
        );
      if (error) throw error;
    },
  };
})();

// 오버레이(익명)용 룰 번들 — 공개 RPC 조립본 { keywordRules, donationRules, donationEnabled }
const BroadcastRules = {
  fetchPublic: async (petId) => {
    const empty = { keywordRules: [], donationRules: [], donationEnabled: true };
    if (!petId) return empty;
    const { data, error } = await supabase.rpc("get_public_broadcast_rules", { p_pet_id: petId });
    if (error) {
      console.error(error);
      return empty;
    }
    return data || empty;
  },
};

// 공개 펫 랭킹 (레벨 desc). RPC로 안전 필드만(이메일 등 제외).
const PetRanking = {
  fetch: async (limit = 20) => {
    const { data, error } = await supabase.rpc("get_pet_ranking", { p_limit: limit });
    if (error) {
      console.error(error);
      return [];
    }
    return (data || []).map((r) => ({
      petName: r.pet_name,
      modelUrl: r.model_url,
      level: r.level,
      totalExp: r.total_exp,
      ownerName: r.owner_name,
      ownerImageUrl: r.owner_image_url || null,
    }));
  },
};

// 펫 검색 API(search_pets)는 SupabaseFanApi.Pet 로 이관됨.

// PET — 상태 표시 (pet_agg_vital은 worker가 service_role로 갱신, 클라는 읽기만)
const PetStatus = (() => {
  return {
    // mood 변환 구간 — ssot.PET_MOOD_MAP. 내림차순 버킷 배열 [{ min, id, icon, label }] (공개 읽기).
    // (구 fetchStatusMap 대체: status_id 직접 저장 → mood_meter 수치 → 버킷 변환)
    fetchMoodMap: async () => {
      const { data } = await supabase.from("ssot").select("value").eq("key", "PET_MOOD_MAP").single();
      return Array.isArray(data && data.value) ? data.value : [];
    },
    // 내 펫의 라이브 미터 (배고픔/에너지/기분/졸림 + 각 그릇). 없으면 기본값.
    // 모든 미터는 "높을수록 좋음" 방향(가득=좋은 상태). max는 성장 시 워커가 상향.
    // awake: 내부는 '말짱함'(high=good)이나 UI 라벨은 '졸림'(=hunger와 동일 관례, 가득=안 졸림).
    fetchMyMeters: async () => {
      // 활성 펫(선택>첫 펫)으로 스코프 — 다묘 유저는 vital 행이 여러 개라 pet_id 필터 필수.
      const petId = await fetchActivePetId();
      const { data, error } = await supabase
        .from("pet_agg_vital")
        .select("hunger_meter, hunger_max, energy_meter, energy_max, mood_meter, mood_max, awake_meter, awake_max")
        .eq("pet_id", petId)
        .maybeSingle();
      if (error) {
        console.error(error);
      }
      return {
        hunger: (data && data.hunger_meter) ?? 0,
        hungerMax: (data && data.hunger_max) || 100,
        energy: (data && data.energy_meter) ?? 0,
        energyMax: (data && data.energy_max) || 100,
        mood: (data && data.mood_meter) ?? 0,
        moodMax: (data && data.mood_max) || 100,
        awake: (data && data.awake_meter) ?? 0,
        awakeMax: (data && data.awake_max) || 100,
      };
    },
  };
})();

// 펫 어그리거트(vital/growth/stat/soul/acc)를 한 번에. 영역 추가 시 RPC만 확장하면 됨.
const PetAgg = (() => {
  return {
    // petId 생략 시 활성 펫(서버에서 selected>first 해석). 반환: { pet_id, vital, growth, stat, soul, acc }
    fetch: async (petId = null) => {
      const { data, error } = await supabase.rpc("fetch_pet_agg", petId ? { p_pet_id: petId } : {});
      if (error) {
        console.error(error);
        return null;
      }
      return data || null;
    },
  };
})();

// 치지직 공식 OAuth 연동 상태. 토큰 컬럼은 컬럼 grant로 비노출 → 안전 컬럼만 조회.
const ChzzkOAuth = (() => {
  return {
    // 본인 행 연동 상태. 미연동이면 null.
    // ⚠️ user_id 명시 필터 필수 — 관리자(admin_read_platform_oauth)는 전체 행이 보여 .maybeSingle() 다중행 에러.
    fetchConnection: async () => {
      if (!_authUserId) return null;
      const { data, error } = await supabase
        .from("platform_oauth")
        .select("channel_id, channel_name, is_connected, updated_at")
        .eq("user_id", _authUserId)
        .eq("provider", "chzzk")
        .maybeSingle();
      if (error) {
        console.error(error);
        return null;
      }
      return data || null;
    },
  };
})();

// 플랫폼 연동 상태 (platform_oauth, 멀티벤더). 토큰 컬럼은 grant로 비노출 → 안전 컬럼만. 본인 행(RLS).
const PlatformOAuth = (() => {
  return {
    // provider별 연동 상태 맵 { chzzk: {channel_name, is_connected, ...}, twitch: {...} }
    // ⚠️ user_id 명시 필터 필수 — 관리자(admin_read_platform_oauth)는 전체 사용자 행이 보여 맵이 뒤섞임.
    fetchConnections: async () => {
      if (!_authUserId) return {};
      const { data, error } = await supabase
        .from("platform_oauth")
        .select("provider, channel_id, channel_name, is_connected, updated_at")
        .eq("user_id", _authUserId);
      if (error) {
        console.error(error);
        return {};
      }
      const map = {};
      for (const row of data || []) map[row.provider] = row;
      return map;
    },
    // chzzk_relay(관리자 relay 대상 채널) 저장/해제는 platform_oauth가 클라 write 금지(42501)라
    // Edge 경유 → client/src/api/ChzzkRelayApi.js (signaling/chzzk/relay/*). 여기선 읽기만 제공.
  };
})();

// 플랫폼 활성 토글 (user_platform_enabled). 비밀 없음 → 클라 직접 RW(RLS=본인).
const UserPlatformEnabled = (() => {
  const COLS = ["chzzk", "twitch", "youtube", "soop"];
  return {
    // { chzzk, twitch, youtube, soop } (없으면 {}).
    fetch: async () => {
      if (!_authUserId) return {};
      const { data, error } = await supabase
        .from("user_platform_enabled")
        .select("chzzk, twitch, youtube, soop")
        .eq("user_id", _authUserId)
        .maybeSingle();
      if (error) {
        console.error(error);
        return {};
      }
      return data || {};
    },
    setEnabled: async (provider, value) => {
      if (!_authUserId) throw new Error("AUTH_USER_ID_NOT_FOUND");
      if (!COLS.includes(provider)) throw new Error("INVALID_PROVIDER");
      const { error } = await supabase
        .from("user_platform_enabled")
        .upsert(
          { user_id: _authUserId, [provider]: value, updated_at: new Date().toISOString() },
          { onConflict: "user_id" },
        );
      if (error) throw error;
    },
  };
})();

// 사용자 상태 — 활성(선택) 펫. user_status.selected_pet_id (RLS=본인).
const UserStatus = (() => {
  return {
    // 선택 펫 id (없으면 null). App.vue 부트스트랩에서 1회 로드해 캐시(setSelectedPetId).
    fetchSelectedPetId: async () => {
      if (!_authUserId) return null;
      const { data, error } = await supabase
        .from("user_status")
        .select("selected_pet_id")
        .eq("user_id", _authUserId)
        .maybeSingle();
      if (error) {
        console.error(error);
        return null;
      }
      return (data && data.selected_pet_id) || null;
    },
    // 활성 펫 변경 (펫 선택 페이지 확인). upsert + 캐시 즉시 갱신.
    setSelectedPet: async (petId) => {
      if (!_authUserId) throw new Error("AUTH_USER_ID_NOT_FOUND");
      const { error } = await supabase
        .from("user_status")
        .upsert(
          { user_id: _authUserId, selected_pet_id: petId, updated_at: new Date().toISOString() },
          { onConflict: "user_id" },
        );
      if (error) throw error;
      setSelectedPetId(petId);
    },
  };
})();

// 분석용 클릭/이벤트 트래킹 — 날짜별 전역 total 카운터(metric_user_click_tracking) +1. fire-and-forget.
const Metric = (() => {
  return {
    trackClick: async (eventKey) => {
      if (isLocal()) return; // 로컬 개발 환경에선 적재 안 함
      if (!_authUserId || !eventKey) return;
      // 날짜별 전역 total 방식 — RPC로 오늘 (event_key,date) 카운터 +1 (원자적, 대문자 강제)
      const { error } = await supabase.rpc("bump_click_metric", { p_event_key: String(eventKey).toUpperCase() });
      if (error) console.error(error);
    },
  };
})();

// 펫스토어 아이템 (통합 item 테이블). 읽기 전용. type: FOOD/HOUSE/BACKGROUND/ACCESSORY.
// 스토어는 노출 아이템만(is_show). 타입 고유 필드(예 FOOD의 hunger/energy)는 attrs에서 펼침.
const Item = (() => {
  return {
    fetchByType: async (type) => {
      const { data, error } = await supabase
        .from("item")
        .select("id, name, price, description, emoji, image_url, attrs")
        .eq("type", type)
        // admin이 봐도 숨김 유지 — RLS와 별개로 명시 필터
        .eq("is_show", true)
        .order("sort_order", { ascending: true });
      if (error) {
        console.error(error);
        return [];
      }
      // 카드 템플릿 호환: description→desc, image_url→imageUrl, attrs(hunger/energy 등) 펼침
      return (data || []).map((r) => ({
        id: r.id,
        name: r.name,
        price: r.price,
        desc: r.description,
        emoji: r.emoji,
        imageUrl: r.image_url,
        ...(r.attrs || {}),
      }));
    },
    // 내 보유 인벤토리 → { [itemId]: qty }. RLS(user_item_select_own)로 본인분만.
    fetchOwnedQtyMap: async () => {
      const { data, error } = await supabase.from("user_item").select("item_id, qty");
      if (error) {
        console.error(error);
        return {};
      }
      const map = {};
      (data || []).forEach((r) => {
        map[r.item_id] = r.qty;
      });
      return map;
    },
    // 내 보유 인벤토리 + 아이템 상세 (보유 아이템 페이지). RPC(get_my_inventory)로 숨김 아이템도 포함.
    fetchInventory: async () => {
      const { data, error } = await supabase.rpc("get_my_inventory");
      if (error) {
        console.error(error);
        return [];
      }
      return (data || []).map((r) => ({
        id: r.item_id,
        qty: r.qty,
        usesLeft: r.uses_left, // 장난감 잔여 내구도 (음식 등은 null)
        name: r.name,
        type: r.type,
        price: r.price,
        emoji: r.emoji,
        imageUrl: r.image_url,
        ...(r.attrs || {}),
      }));
    },
    // 밥주기는 pet Edge Function(PetApi.feed) → feed_pet RPC로 이동 (인벤토리 차감 + 회복 + 보너스).

    // 내가 보유한 배경 → [{ label, fileName }]. 펫세팅 배경설정 목록용(보유분만 노출).
    //  file_name = 적용 연결키(resource_lottie.file_name). get_my_inventory RPC로 숨김 포함 보유 조회.
    fetchOwnedBackgrounds: async () => {
      const { data, error } = await supabase.rpc("get_my_inventory");
      if (error) {
        console.error(error);
        return [];
      }
      return (data || [])
        .filter((r) => r.type === "BACKGROUND")
        .map((r) => ({ label: r.name, fileName: (r.attrs || {}).file_name }))
        .filter((x) => x.fileName);
    },
  };
})();

export default {
  setAuthUserId,
  getAuthUserId,
  setPetId,
  setSelectedPetId,
  Metric,
  Item,

  //
  updateProfileName,

  //
  fetchMyProfile,
  //
  fetchMyVirtupetLevelMeta,
  upsertVirtupetLevel,

  UserAccount,
  UserAccountOnboarding,
  BillingPurchase,
  Reaction,
  ReactionKeywordRule,
  ReactionKeywordSessionRule,
  ReactionDonationRule,
  UserBroadcastSetting,
  BroadcastRules,
  PetRanking,
  PetProfile,
  PetBroadcastSetting,
  PetStatus,
  PetAgg,
  ChzzkOAuth,
  PlatformOAuth,
  UserPlatformEnabled,
  UserStatus,
};
